import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Refrigerator,
  Brain,
  ShoppingCart,
  Users,
  Globe,
  TrendingUp,
  ArrowRight,
  Check,
  Sparkles,
  Clock,
  DollarSign,
  Leaf,
  X,
  Loader2,
  Mail,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { LANDING } from "@/i18n/landing";
import { PATH_LANG, LANG_PATH } from "@/lib/langRoutes";

const LANG_FLAGS = { uk: "🇺🇦", en: "🇬🇧", ru: "🇷🇺" };
const LANG_NAMES = { uk: "Українська", en: "English", ru: "Русский" };

export default function Landing() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t, lang, switchLang } = useLanguage();
  const { api } = useAuth();
  const T = LANDING[lang] || LANDING.en;

  const goToSignup = () => navigate("/auth?mode=signup");
  const goToSignin = () => navigate("/auth?mode=signin");

  // The address decides the language here, not localStorage: someone arriving on
  // /uk from a search result must read Ukrainian even if they once clicked
  // English on this device. Only ever runs for signed-out visitors, because a
  // signed-in one gets the app at these paths and never renders this page -
  // which is what keeps switchLang from overwriting an account's saved language.
  useEffect(() => {
    // Same trailing-slash tolerance as the router in App: /uk/ and /uk are one
    // address as far as anyone linking to us is concerned.
    const fromPath = PATH_LANG[pathname.replace(/\/+$/, "") || "/"];
    if (fromPath && fromPath !== lang) switchLang(fromPath);
  }, [pathname, lang, switchLang]);

  // Switching language moves to that language's address, so what is shown and
  // what is in the address bar cannot disagree - and so a shared link carries
  // the language with it.
  //
  // A full load rather than a client-side navigation: title, description,
  // canonical and <html lang> live in the HTML document, and routing to another
  // path leaves all four describing the language the visitor just left. People
  // change language about once, so the reload costs nothing worth saving.
  const chooseLang = (code) => {
    switchLang(code);
    setShowLangPicker(false);
    const target = LANG_PATH[code];
    if (target && target !== pathname) window.location.assign(target);
  };

  const [showLangPicker, setShowLangPicker] = useState(false);
  const [billing, setBilling] = useState("annual"); // annual | monthly

  // Individual benefits reuse the same keys as the Subscription page
  // (single source of truth in i18n/translations.js).
  const indivBenefits = [
    t("subBenefit1"),
    t("subBenefit2"),
    t("subBenefit3"),
    t("subBenefit4"),
    t("subBenefit5"),
    t("subBenefit6"),
    t("subBenefit7"),
  ];

  // Notify-me (Family waitlist)
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  // idle | loading | success | already | error | invalid
  const [notifyState, setNotifyState] = useState("idle");

  const openNotify = () => {
    setNotifyEmail("");
    setNotifyState("idle");
    setNotifyOpen(true);
  };
  const closeNotify = () => setNotifyOpen(false);

  const submitNotify = async () => {
    const email = notifyEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNotifyState("invalid");
      return;
    }
    setNotifyState("loading");
    try {
      const res = await api().post("/waitlist", { email, plan: "family", lang });
      setNotifyState(res?.data?.already ? "already" : "success");
    } catch (e) {
      setNotifyState("error");
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#2C2C2C] antialiased">
      {/* ───────── Navbar ───────── */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/mealpill-icon.svg" alt="MealPill" className="w-8 h-8" />
            <span className="font-semibold text-lg tracking-tight">MealPill</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language switcher */}
            <div className="relative">
              <button
                onClick={() => setShowLangPicker((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-black/10 hover:border-black/20 transition text-sm"
                aria-label="Language"
              >
                <span>{LANG_FLAGS[lang]}</span>
                <Globe className="w-4 h-4 text-[#5C5C5C]" />
              </button>
              {showLangPicker && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowLangPicker(false)}
                  />
                  <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-lg border border-black/5 py-1 z-50 min-w-[160px]">
                    {Object.keys(LANG_FLAGS).map((code) => (
                      <button
                        key={code}
                        onClick={() => chooseLang(code)}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left transition ${
                          lang === code
                            ? "bg-[#7B9E7F]/10 text-[#5C7A5E] font-medium"
                            : "hover:bg-black/5"
                        }`}
                      >
                        <span>{LANG_FLAGS[code]}</span>
                        <span>{LANG_NAMES[code]}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <button
              onClick={goToSignin}
              className="text-sm text-[#5C5C5C] hover:text-[#2C2C2C] px-3 py-2 transition"
            >
              {T.navSignin}
            </button>
            <button
              onClick={goToSignup}
              className="bg-[#FFB088] hover:bg-[#FF9F73] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition shadow-sm"
            >
              {T.navTry}
            </button>
          </div>
        </div>
      </nav>

      {/* ───────── Hero ───────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-12 pb-20 lg:pt-20 lg:pb-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#A8D5BA]/30 border border-[#7B9E7F]/20 text-xs font-medium text-[#5C7A5E] mb-6">
              <Sparkles className="w-3 h-3" />
              {T.heroBadge}
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-6">
              {T.heroTitle1}{" "}
              <span className="text-[#7B9E7F]">{T.heroTitleAccent}</span>
            </h1>

            <p className="text-xl lg:text-2xl text-[#5C5C5C] leading-snug mb-8 max-w-lg">
              {T.heroSub1}
              <br />
              {T.heroSub2}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <button
                onClick={goToSignup}
                className="group bg-[#FFB088] hover:bg-[#FF9F73] text-white font-semibold px-7 py-4 rounded-full transition shadow-lg shadow-[#FFB088]/30 inline-flex items-center gap-2"
              >
                {T.heroCta}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
              </button>
              <p className="text-sm text-[#7C7C7C]">{T.heroNote}</p>
            </div>
          </div>

          {/* Right: floating cards mockup. Decorative, but not exempt from
              translation: a Ukrainian reader seeing an English product
              interface reads it as a screenshot of somebody else's app. */}
          <div className="relative h-[480px] lg:h-[560px]">
            {/* Backdrop gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#A8D5BA]/40 via-[#FAF0E5] to-[#FFB088]/30 rounded-[2rem]" />

            {/* Card 1: meal plan */}
            <div className="absolute top-10 left-6 lg:left-10 bg-white rounded-2xl shadow-xl shadow-black/5 p-5 w-60 z-10 -rotate-3">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-[#A8D5BA]/40 flex items-center justify-center">
                  <Brain className="w-4 h-4 text-[#7B9E7F]" />
                </div>
                <span className="text-xs font-semibold text-[#5C5C5C]">{T.heroMockPlan}</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-base">🥑</span>
                  <span>{T.heroMockDish1}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-base">🍝</span>
                  <span>{T.heroMockDish2}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-base">🐟</span>
                  <span>{T.heroMockDish3}</span>
                </div>
              </div>
            </div>

            {/* Card 2: fridge */}
            <div className="absolute top-28 right-4 lg:right-6 bg-white rounded-2xl shadow-xl shadow-black/5 p-5 w-60 z-20 rotate-2">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#FFB088]/30 flex items-center justify-center">
                    <Refrigerator className="w-4 h-4 text-[#D2735A]" />
                  </div>
                  <span className="text-xs font-semibold text-[#5C5C5C]">{T.heroMockFridge}</span>
                </div>
                <span className="text-[10px] text-[#7B9E7F] font-medium px-2 py-0.5 rounded-full bg-[#A8D5BA]/30">
                  {T.heroMockItems}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {["🥬", "🍅", "🧅", "🥕", "🥚", "🧀", "🥛", "🍋"].map((emoji, i) => (
                  <div
                    key={i}
                    className="aspect-square bg-[#FAF0E5] rounded-lg flex items-center justify-center text-lg"
                  >
                    {emoji}
                  </div>
                ))}
              </div>
            </div>

            {/* Card 3: shopping list */}
            <div className="absolute bottom-6 left-10 lg:left-16 bg-white rounded-2xl shadow-xl shadow-black/5 p-5 w-56 z-10 -rotate-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-[#E9C46A]/30 flex items-center justify-center">
                  <ShoppingCart className="w-4 h-4 text-[#B8941F]" />
                </div>
                <span className="text-xs font-semibold text-[#5C5C5C]">{T.heroMockList}</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-3.5 h-3.5 text-[#7B9E7F]" />
                  <span>{T.heroMockBuy1}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-3.5 h-3.5 text-[#7B9E7F]" />
                  <span>{T.heroMockBuy2}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-3.5 h-3.5 text-[#7B9E7F]" />
                  <span>{T.heroMockBuy3}</span>
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute top-2 right-10 z-30 bg-white rounded-full shadow-lg shadow-black/5 px-3 py-1.5 flex items-center gap-1.5 rotate-6">
              <Leaf className="w-3.5 h-3.5 text-[#7B9E7F]" />
              <span className="text-xs font-semibold">{T.heroMockBadge}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Problem stats ───────── */}
      <section className="bg-white border-y border-black/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#7B9E7F] uppercase tracking-widest mb-3">
              {T.realityLabel}
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl mx-auto">
              {T.realityTitle}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#FFB088]/15 text-[#D2735A] mb-5">
                <DollarSign className="w-7 h-7" />
              </div>
              <div className="text-4xl lg:text-5xl font-bold mb-2">{T.stat1Num}</div>
              <p className="text-[#5C5C5C] text-base">{T.stat1Text}</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#A8D5BA]/30 text-[#5C7A5E] mb-5">
                <Clock className="w-7 h-7" />
              </div>
              <div className="text-4xl lg:text-5xl font-bold mb-2">{T.stat2Num}</div>
              <p className="text-[#5C5C5C] text-base">{T.stat2Text}</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#E9C46A]/20 text-[#B8941F] mb-5">
                <TrendingUp className="w-7 h-7" />
              </div>
              <div className="text-4xl lg:text-5xl font-bold mb-2">{T.stat3Num}</div>
              <p className="text-[#5C5C5C] text-base">{T.stat3Text}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── How it works ───────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
        <div className="text-center mb-16">
          <p className="text-sm font-semibold text-[#7B9E7F] uppercase tracking-widest mb-3">
            {T.howLabel}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl mx-auto">
            {T.howTitle}
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {[
            {
              num: "01",
              icon: Refrigerator,
              title: T.step1Title,
              text: T.step1Text,
              color: "#A8D5BA",
              textColor: "#5C7A5E",
            },
            {
              num: "02",
              icon: Brain,
              title: T.step2Title,
              text: T.step2Text,
              color: "#FFB088",
              textColor: "#D2735A",
            },
            {
              num: "03",
              icon: ShoppingCart,
              title: T.step3Title,
              text: T.step3Text,
              color: "#E9C46A",
              textColor: "#B8941F",
            },
          ].map((step) => (
            <div key={step.num} className="relative">
              <div className="text-7xl lg:text-8xl font-bold text-[#F0EDE5] mb-2 leading-none">
                {step.num}
              </div>
              <div
                className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4"
                style={{ backgroundColor: `${step.color}40`, color: step.textColor }}
              >
                <step.icon className="w-6 h-6" />
              </div>
              <h3 className="text-xl lg:text-2xl font-bold mb-3 tracking-tight">
                {step.title}
              </h3>
              <p className="text-[#5C5C5C] leading-relaxed">{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ───────── DEMO ENTRY ───────── */}
      {/* Sits straight after the three steps: the reader has just been told how
          it works, which is exactly when they start wondering whether it really
          does. Answers that, rather than repeating the trial CTA. */}
      {/* Full-bleed green band, the same family as the closing CTA. This used to
          be a white card on a white page behind a hairline border, so the one
          place a visitor can touch the product read as decoration. The gradient
          is a step darker than the closing CTA on purpose: white body copy on
          #7B9E7F sits at 3.0:1, which is below AA. */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#5C7A5E] to-[#4A6350]">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-[#A8D5BA]/25 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#FFB088]/20 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Copy */}
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-white/70 mb-3">
                {T.demoLabel}
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1] mb-4">
                {T.demoTitle}
              </h2>
              <p className="text-lg leading-relaxed text-white/90 mb-8">{T.demoText}</p>
              <a
                href="/demo"
                className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-semibold text-[#2C2C2C] shadow-xl transition hover:bg-[#FAF0E5]"
              >
                {T.demoCta}
                <ArrowRight className="w-4 h-4 transition group-hover:translate-x-0.5" />
              </a>
              <p className="mt-3 text-sm text-white/60">{T.demoNote}</p>
            </div>

            {/* A result, not a button: the same colour coding the planner uses.
                Prices are ranges because that is what the demo itself returns -
                a point estimate here would promise a precision the product
                stops claiming two clicks later. */}
            <div className="rounded-2xl bg-white p-5 lg:p-6 shadow-2xl">
              {[
                { label: T.demoB, name: T.demoDish1, accent: "#E9C46A", kcal: 280, price: "CA$1.70-2.40" },
                { label: T.demoL, name: T.demoDish2, accent: "#7B9E7F", kcal: 450, price: "CA$4.20-6.10" },
                { label: T.demoD, name: T.demoDish3, accent: "#D2735A", kcal: 320, price: "CA$3.80-5.40" },
              ].map((m, i) => (
                <div key={i} className="mb-3 last:mb-0">
                  <p className="text-[11px] uppercase tracking-widest text-[#5C5C5C]/40 mb-1">
                    {m.label}
                  </p>
                  <div
                    className="flex items-center gap-3 rounded-xl bg-white border-l-[3px] px-4 py-3 shadow-[0_1px_3px_rgba(0,0,0,0.06)]"
                    style={{ borderLeftColor: m.accent }}
                  >
                    <span className="flex-1 text-sm font-medium">{m.name}</span>
                    <span className="text-xs text-[#5C5C5C]/50">{m.kcal} {T.demoKcal}</span>
                    <span className="text-xs text-[#5C5C5C]/70">{m.price}</span>
                  </div>
                </div>
              ))}
              <div className="mt-4 flex items-center justify-between border-t border-black/5 pt-3">
                <span className="text-xs text-[#5C5C5C]/60">{T.demoTotal}</span>
                <span className="text-sm font-semibold">CA$9.70-13.90</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Features grid ───────── */}
      <section className="bg-[#FAF0E5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#7B9E7F] uppercase tracking-widest mb-3">
              {T.featLabel}
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl mx-auto">
              {T.featTitle}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Refrigerator, title: T.feat1Title, text: T.feat1Text },
              { icon: Brain, title: T.feat2Title, text: T.feat2Text },
              { icon: ShoppingCart, title: T.feat3Title, text: T.feat3Text },
              { icon: Users, title: T.feat4Title, text: T.feat4Text },
              { icon: Globe, title: T.feat5Title, text: T.feat5Text },
              { icon: TrendingUp, title: T.feat6Title, text: T.feat6Text },
            ].map((feature, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-7 border border-black/5 hover:shadow-lg hover:border-[#7B9E7F]/30 transition"
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#A8D5BA]/30 text-[#5C7A5E] mb-4">
                  <feature.icon className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-lg mb-2 tracking-tight">{feature.title}</h3>
                <p className="text-[#5C5C5C] text-sm leading-relaxed">{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── Pricing ───────── */}
      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
        <div className="text-center mb-10">
          <p className="text-sm font-semibold text-[#7B9E7F] uppercase tracking-widest mb-3">
            {T.priceLabel}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl mx-auto mb-4">
            {T.priceTitle}
          </h2>
          <p className="text-[#5C5C5C] text-lg max-w-xl mx-auto">{T.priceSub}</p>
        </div>

        {/* Billing period toggle (segmented control, same style as Subscription page) */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex gap-1.5 bg-white rounded-2xl p-1.5 border border-black/5 shadow-sm">
            <button
              onClick={() => setBilling("annual")}
              className={`px-5 py-2 rounded-xl text-sm font-semibold transition ${
                billing === "annual"
                  ? "bg-[#7B9E7F] text-white"
                  : "text-[#5C5C5C] hover:bg-black/5"
              }`}
            >
              {T.billAnnual}
              <span
                className={`ml-1.5 text-[11px] ${
                  billing === "annual" ? "text-white/85" : "text-[#7B9E7F]"
                }`}
              >
                · {T.annualSave}
              </span>
            </button>
            <button
              onClick={() => setBilling("monthly")}
              className={`px-5 py-2 rounded-xl text-sm font-semibold transition ${
                billing === "monthly"
                  ? "bg-[#7B9E7F] text-white"
                  : "text-[#5C5C5C] hover:bg-black/5"
              }`}
            >
              {T.billMonthly}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Individual — active */}
          <div className="bg-white rounded-3xl p-8 border-2 border-[#7B9E7F]/30 shadow-lg shadow-[#7B9E7F]/5 flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xl font-bold">{T.planIndividual}</h3>
            </div>
            <p className="text-sm text-[#7C7C7C] mb-6">{T.planIndividualDesc}</p>

            <div className="mb-6">
              <div className="flex items-baseline gap-1.5">
                <span className="text-4xl font-bold">
                  {billing === "annual" ? "CA$89.99" : "CA$8.99"}
                </span>
                <span className="text-[#7C7C7C]">
                  {billing === "annual" ? T.perYr : T.perMo}
                </span>
              </div>
              {billing === "annual" && (
                <div className="mt-1 text-xs font-semibold text-[#5C7A5E]">
                  {T.annualSave}
                </div>
              )}
            </div>

            <ul className="space-y-2.5 mb-8 flex-1">
              {indivBenefits.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-[#3C3C3C]"
                >
                  <Check className="w-4 h-4 text-[#7B9E7F] mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={goToSignup}
              className="w-full bg-[#FFB088] hover:bg-[#FF9F73] text-white font-semibold px-6 py-3.5 rounded-full transition shadow-sm"
            >
              {T.individualCta}
            </button>
          </div>

          {/* Family — coming soon */}
          <div className="bg-white/60 rounded-3xl p-8 border border-black/5 flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xl font-bold text-[#5C5C5C]">{T.planFamily}</h3>
              <span className="text-[11px] font-semibold text-[#B8941F] bg-[#E9C46A]/20 px-2.5 py-1 rounded-full">
                {T.comingSoon}
              </span>
            </div>
            <p className="text-sm text-[#7C7C7C] mb-6">{T.planFamilyDesc}</p>

            <div className="mb-6">
              <div className="flex items-baseline gap-1.5">
                <span className="text-4xl font-bold text-[#7C7C7C]">
                  {billing === "annual" ? "CA$139.99" : "CA$13.99"}
                </span>
                <span className="text-[#9C9C9C]">
                  {billing === "annual" ? T.perYr : T.perMo}
                </span>
              </div>
              {billing === "annual" && (
                <div className="mt-1 text-xs font-semibold text-[#9C9C9C]">
                  {T.annualSave}
                </div>
              )}
            </div>

            <ul className="space-y-2.5 mb-8 flex-1">
              {[T.familyF1, T.familyF2, T.familyF3, T.familyF4].map((f, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-[#7C7C7C]"
                >
                  <Check className="w-4 h-4 text-[#A8D5BA] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={openNotify}
              className="w-full bg-white border-2 border-[#7B9E7F]/40 hover:border-[#7B9E7F] text-[#5C7A5E] font-semibold px-6 py-3.5 rounded-full transition inline-flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" />
              {T.familyNotifyCta}
            </button>
          </div>
        </div>
      </section>

      {/* ───────── Final CTA ───────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <div className="bg-gradient-to-br from-[#7B9E7F] to-[#5C7A5E] rounded-[2rem] p-12 lg:p-20 text-center relative overflow-hidden">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-[#A8D5BA]/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[#FFB088]/30 rounded-full blur-3xl" />

          <div className="relative">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4 max-w-2xl mx-auto leading-[1.1]">
              {T.finalTitle}
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">{T.finalSub}</p>
            <button
              onClick={goToSignup}
              className="group bg-white hover:bg-[#FAF0E5] text-[#2C2C2C] font-semibold px-8 py-4 rounded-full transition shadow-xl inline-flex items-center gap-2"
            >
              {T.finalCta}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
            </button>
          </div>
        </div>
      </section>

      {/* ───────── Footer ───────── */}
      <footer className="bg-white border-t border-black/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <img src="/mealpill-icon.svg" alt="MealPill" className="w-7 h-7" />
                <span className="font-semibold text-lg">MealPill</span>
              </div>
              <p className="text-sm text-[#7C7C7C] max-w-xs">{T.footerTagline}</p>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-3">{T.footerLegal}</h4>
              <ul className="space-y-2 text-sm text-[#7C7C7C]">
                <li>
                  <a href="/privacy" className="hover:text-[#2C2C2C] transition">
                    {T.footerPrivacy}
                  </a>
                </li>
                <li>
                  <a href="/terms" className="hover:text-[#2C2C2C] transition">
                    {T.footerTerms}
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-3">{T.footerContact}</h4>
              <ul className="space-y-2 text-sm text-[#7C7C7C]">
                <li>
                  <a
                    href="mailto:hello@mealpill.app"
                    className="hover:text-[#2C2C2C] transition"
                  >
                    hello@mealpill.app
                  </a>
                </li>
                <li>{T.footerLocation}</li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-black/5 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-[#7C7C7C]">{T.footerCopyright}</p>
            <p className="text-xs text-[#7C7C7C]">{T.footerSolo}</p>
          </div>
        </div>
      </footer>

      {/* ───────── Notify-me modal (Family waitlist) ───────── */}
      {notifyOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeNotify}
          />
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
            <button
              onClick={closeNotify}
              className="absolute top-4 right-4 text-[#9C9C9C] hover:text-[#2C2C2C] transition"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {notifyState === "success" || notifyState === "already" ? (
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#A8D5BA]/40 text-[#5C7A5E] mb-4">
                  <Check className="w-7 h-7" />
                </div>
                <p className="text-lg font-semibold mb-6">
                  {notifyState === "already" ? T.notifyAlready : T.notifySuccess}
                </p>
                <button
                  onClick={closeNotify}
                  className="bg-[#7B9E7F] hover:bg-[#5C7A5E] text-white font-semibold px-6 py-3 rounded-full transition"
                >
                  OK
                </button>
              </div>
            ) : (
              <>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#FFB088]/20 text-[#D2735A] mb-4">
                  <Mail className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">{T.notifyTitle}</h3>
                <p className="text-sm text-[#5C5C5C] mb-5">{T.notifySub}</p>

                <input
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => {
                    setNotifyEmail(e.target.value);
                    if (notifyState === "invalid" || notifyState === "error")
                      setNotifyState("idle");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitNotify();
                  }}
                  placeholder={T.notifyPlaceholder}
                  className="w-full px-4 py-3 rounded-xl border border-black/10 focus:border-[#7B9E7F] focus:outline-none focus:ring-2 focus:ring-[#7B9E7F]/20 mb-2 transition"
                />

                {notifyState === "invalid" && (
                  <p className="text-xs text-[#D2735A] mb-2">{T.notifyInvalid}</p>
                )}
                {notifyState === "error" && (
                  <p className="text-xs text-[#D2735A] mb-2">{T.notifyError}</p>
                )}

                <button
                  onClick={submitNotify}
                  disabled={notifyState === "loading"}
                  className="w-full bg-[#FFB088] hover:bg-[#FF9F73] disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-full transition inline-flex items-center justify-center gap-2 mb-3"
                >
                  {notifyState === "loading" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  {T.notifySubmit}
                </button>

                <p className="text-center text-xs text-[#9C9C9C]">{T.notifyPromise}</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
